// CentralizationManager.js - Enhanced with Easy Assignment Features

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faLayerGroup, faStore, faUsers, faPlus, faEdit, faTrash,
    faTimes, faSave, faUserPlus, faUserMinus, faCheck,
    faExclamationTriangle, faSync, faBox, faCrown,
    faShare, faLink, faUnlink, faEye, faEyeSlash,
    faChevronDown, faChevronUp, faSearch, faFilter,
    faArrowRight, faArrowLeft, faBuilding, faTags,
    faUserCircle, faUserCog, faUserTag, faInfoCircle,
    faList, faThLarge, faSlidersH, faClock, faHistory,
    faUserCheck, faStoreAlt, faBoxes, faUsersCog,
    faRocket, faMagic, faWandMagicSparkles, faBolt,
    faCircleCheck, faCircleXmark, faClockRotateLeft,
    faArrowUpRightFromSquare, faArrowDownLeftFromSquare
} from '@fortawesome/free-solid-svg-icons';
import "../styles/root_centralization.css";

const CentralizationManager = ({ user, darkMode, translations, showToast }) => {
    // ==============================================
    // STATE
    // ==============================================
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    
    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddShopModal, setShowAddShopModal] = useState(false);
    const [showAssignProductModal, setShowAssignProductModal] = useState(false);
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);
    const [showGroupDetailsModal, setShowGroupDetailsModal] = useState(false);
    const [showQuickAssignModal, setShowQuickAssignModal] = useState(false);
    const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
    
    // Data states
    const [availableShops, setAvailableShops] = useState([]);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [allUsersWithRoles, setAllUsersWithRoles] = useState([]);
    const [groupDetails, setGroupDetails] = useState(null);
    const [stockMovements, setStockMovements] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all'); // 'all', 'shop', 'user', 'product'
    
    // Loading states
    const [submitting, setSubmitting] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [activeTab, setActiveTab] = useState('groups');
    
    // Product table options
    const [productTables] = useState([
        { value: 'mechanics_products', label: 'Mechanics Products' },
        { value: 'pos_products', label: 'POS Products' },
        { value: 'cosmetics_products', label: 'Cosmetics Products' },
        { value: 'pharmacy_products', label: 'Pharmacy Products' }
    ]);

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0
    });

    // ==============================================
    // FORM STATES
    // ==============================================
    const [groupForm, setGroupForm] = useState({
        group_name: '',
        group_code: '',
        description: '',
        shop_ids: []
    });

    const [editGroupForm, setEditGroupForm] = useState({
        group_id: '',
        group_name: '',
        description: '',
        status: 'active'
    });

    const [addShopForm, setAddShopForm] = useState({
        group_id: '',
        shop_ids: []
    });

    const [assignProductForm, setAssignProductForm] = useState({
        group_id: '',
        product_id: '',
        product_table: 'mechanics_products',
        status: 'active'
    });

    const [addUserForm, setAddUserForm] = useState({
        group_id: '',
        user_id: '',
        access_level: 'user'
    });

    const [quickAssignForm, setQuickAssignForm] = useState({
        type: 'shop', // 'shop', 'user', 'product'
        group_id: '',
        items: [],
        search: '',
        selectAll: false
    });

    const [bulkAssignForm, setBulkAssignForm] = useState({
        shop_ids: [],
        user_ids: [],
        product_ids: [],
        group_id: '',
        access_level: 'user'
    });

    const [removeItem, setRemoveItem] = useState({
        type: '',
        id: null,
        group_id: null,
        name: ''
    });

    // ==============================================
    // REFS
    // ==============================================
    const searchInputRef = useRef(null);
    const modalRef = useRef(null);

    // ==============================================
    // HELPERS
    // ==============================================
    const isUserAvailable = user && typeof user === 'object' && user.id;

    // ==============================================
    // API CALLS
    // ==============================================
    
    const fetchUsersWithRoles = useCallback(async () => {
        setLoadingUsers(true);
        setApiError(null);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/root_users.php?action=list');
            const data = await response.json();
            
            if (data.success) {
                setAllUsersWithRoles(data.data || []);
                const activeUsers = (data.data || []).filter(u => u.status === 'active');
                setAvailableUsers(activeUsers);
            } else {
                console.error('Failed to fetch users:', data.error);
                setApiError('Failed to fetch users: ' + (data.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error fetching users with roles:', error);
            setApiError('Network error while fetching users');
        } finally {
            setLoadingUsers(false);
        }
    }, []);

    const fetchGroups = useCallback(async () => {
        if (!isUserAvailable) {
            setLoading(false);
            return;
        }
        
        setLoading(true);
        setApiError(null);
        try {
            let url = 'https://ourzestapp.online/oz_backend/centralization.php?action=get_groups';
            
            if (isUserAvailable) {
                url += `&user_id=${user.id}`;
            }
            
            const response = await fetch(url);
            const data = await response.json();
            
            if (data.success) {
                setGroups(data.data || []);
            } else {
                console.error('Failed to fetch groups:', data.error);
                setApiError('Failed to fetch groups: ' + (data.error || 'Unknown error'));
                if (showToast) showToast('error', data.error || 'Failed to fetch groups');
            }
        } catch (error) {
            console.error('Error fetching groups:', error);
            setApiError('Network error while fetching groups');
            if (showToast) showToast('error', 'Network error');
        } finally {
            setLoading(false);
        }
    }, [user, isUserAvailable, showToast]);

    const fetchAvailableShops = useCallback(async (groupId = null, search = '') => {
        if (!isUserAvailable) return;
        
        setApiError(null);
        try {
            let url = 'https://ourzestapp.online/oz_backend/centralization.php?action=get_available_shops';
            
            if (isUserAvailable) {
                url += `&user_id=${user.id}`;
            }
            
            if (groupId) {
                url += `&group_id=${groupId}`;
            }
            
            if (search) {
                url += `&search=${encodeURIComponent(search)}`;
            }
            
            const response = await fetch(url);
            const data = await response.json();
            
            if (data.success) {
                setAvailableShops(data.data || []);
            } else {
                console.error('Failed to fetch available shops:', data.error);
                setApiError('Failed to fetch shops: ' + (data.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error fetching available shops:', error);
            setApiError('Network error while fetching shops');
        }
    }, [user, isUserAvailable]);

    const fetchAvailableProducts = useCallback(async (groupId, productTable = 'mechanics_products', search = '') => {
        if (!groupId || !isUserAvailable) return;

        setApiError(null);
        try {
            const url = `https://ourzestapp.online/oz_backend/centralization.php?action=get_available_products&group_id=${groupId}&product_table=${productTable}${search ? `&search=${encodeURIComponent(search)}` : ''}`;
            const response = await fetch(url);
            const data = await response.json();
            if (data.success) {
                setAvailableProducts(data.data || []);
            } else {
                console.error('Failed to fetch available products:', data.error);
                setApiError('Failed to fetch products: ' + (data.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error fetching available products:', error);
            setApiError('Network error while fetching products');
        }
    }, [isUserAvailable]);

    const fetchGroupDetails = useCallback(async (groupId) => {
        if (!groupId || !isUserAvailable) return null;
        
        setApiError(null);
        try {
            const response = await fetch(
                `https://ourzestapp.online/oz_backend/centralization.php?action=get_group_details&group_id=${groupId}`
            );
            const data = await response.json();
            if (data.success) {
                setSelectedGroup(data.data);
                setGroupDetails(data.data);
                return data.data;
            } else {
                console.error('Failed to fetch group details:', data.error);
                setApiError('Failed to fetch group details: ' + (data.error || 'Unknown error'));
                return null;
            }
        } catch (error) {
            console.error('Error fetching group details:', error);
            setApiError('Network error while fetching group details');
            return null;
        }
    }, [isUserAvailable]);

    const fetchStockMovements = useCallback(async (groupId = null, productId = null) => {
        if (!isUserAvailable) return;
        
        try {
            let url = 'https://ourzestapp.online/oz_backend/centralization.php?action=get_movements&limit=100';
            if (groupId) {
                url += `&group_id=${groupId}`;
            }
            if (productId) {
                url += `&centralized_product_id=${productId}`;
            }
            
            const response = await fetch(url);
            const data = await response.json();
            if (data.success) {
                setStockMovements(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching stock movements:', error);
        }
    }, [isUserAvailable]);

    // ==============================================
    // HANDLERS - CRUD Operations
    // ==============================================

    const handleCreateGroup = async (e) => {
        e.preventDefault();
        
        if (!isUserAvailable) {
            if (showToast) showToast('error', 'User not authenticated. Please login again.');
            return;
        }

        if (!groupForm.group_name.trim() || !groupForm.group_code.trim()) {
            if (showToast) showToast('error', 'Group name and code are required');
            return;
        }

        setSubmitting(true);
        setApiError(null);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/centralization.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'create_group',
                    group_name: groupForm.group_name,
                    group_code: groupForm.group_code,
                    description: groupForm.description,
                    created_by: user.id,
                    shop_ids: JSON.stringify(groupForm.shop_ids)
                })
            });
            const data = await response.json();
            if (data.success) {
                if (showToast) showToast('success', 'Group created successfully');
                setShowCreateModal(false);
                setGroupForm({ group_name: '', group_code: '', description: '', shop_ids: [] });
                await fetchGroups();
                const newGroupId = data.data?.group_id;
                if (newGroupId) {
                    setExpandedGroups(prev => ({ ...prev, [newGroupId]: true }));
                    await fetchGroupDetails(newGroupId);
                }
            } else {
                if (showToast) showToast('error', data.error || 'Failed to create group');
            }
        } catch (error) {
            if (showToast) showToast('error', 'Network error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddShops = async (e) => {
        e.preventDefault();
        if (!isUserAvailable) {
            if (showToast) showToast('error', 'User not authenticated');
            return;
        }

        if (!addShopForm.shop_ids || addShopForm.shop_ids.length === 0) {
            if (showToast) showToast('error', 'Please select at least one shop');
            return;
        }

        const groupId = addShopForm.group_id;

        setSubmitting(true);
        setApiError(null);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/centralization.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'add_shops_to_group',
                    group_id: groupId,
                    shop_ids: JSON.stringify(addShopForm.shop_ids),
                    added_by: user.id
                })
            });
            const data = await response.json();
            if (data.success) {
                if (showToast) showToast('success', data.message || 'Shops added successfully');
                setShowAddShopModal(false);
                setAddShopForm({ group_id: '', shop_ids: [] });
                await fetchGroups();
                await fetchGroupDetails(groupId);
                setExpandedGroups(prev => ({ ...prev, [groupId]: true }));
            } else {
                if (showToast) showToast('error', data.error || 'Failed to add shops');
            }
        } catch (error) {
            if (showToast) showToast('error', 'Network error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemoveFromGroup = async () => {
        if (!removeItem.group_id || !removeItem.id) return;

        setSubmitting(true);
        try {
            let response;
            if (removeItem.type === 'shop') {
                response = await fetch('https://ourzestapp.online/oz_backend/centralization.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'remove_shops_from_group',
                        group_id: removeItem.group_id,
                        shop_ids: JSON.stringify([removeItem.id])
                    })
                });
            } else if (removeItem.type === 'user') {
                response = await fetch('https://ourzestapp.online/oz_backend/centralization.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'remove_user_from_group',
                        group_id: removeItem.group_id,
                        user_id: removeItem.id
                    })
                });
            } else if (removeItem.type === 'product') {
                response = await fetch('https://ourzestapp.online/oz_backend/centralization.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'remove_product',
                        centralized_product_id: removeItem.id
                    })
                });
            }

            if (response) {
                const data = await response.json();
                if (data.success) {
                    if (showToast) showToast('success', `${removeItem.type} removed successfully`);
                    setShowRemoveConfirmModal(false);
                    const groupId = removeItem.group_id;
                    setRemoveItem({ type: '', id: null, group_id: null, name: '' });
                    await fetchGroups();
                    await fetchGroupDetails(groupId);
                    setExpandedGroups(prev => ({ ...prev, [groupId]: true }));
                } else {
                    if (showToast) showToast('error', data.error || `Failed to remove ${removeItem.type}`);
                }
            }
        } catch (error) {
            if (showToast) showToast('error', 'Network error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAssignProduct = async (e) => {
        e.preventDefault();
        if (!isUserAvailable) {
            if (showToast) showToast('error', 'User not authenticated');
            return;
        }

        if (!assignProductForm.product_id) {
            if (showToast) showToast('error', 'Please select a product');
            return;
        }

        const groupId = assignProductForm.group_id;

        setSubmitting(true);
        setApiError(null);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/centralization.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'assign_product',
                    product_id: assignProductForm.product_id,
                    product_table: assignProductForm.product_table,
                    group_id: groupId,
                    assigned_by: user.id,
                    status: assignProductForm.status
                })
            });
            const data = await response.json();
            if (data.success) {
                if (showToast) showToast('success', 'Product assigned as centralized');
                setShowAssignProductModal(false);
                setAssignProductForm({ group_id: '', product_id: '', product_table: 'mechanics_products', status: 'active' });
                await fetchGroups();
                await fetchGroupDetails(groupId);
                setExpandedGroups(prev => ({ ...prev, [groupId]: true }));
            } else {
                if (showToast) showToast('error', data.error || 'Failed to assign product');
            }
        } catch (error) {
            if (showToast) showToast('error', 'Network error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        if (!isUserAvailable) {
            if (showToast) showToast('error', 'User not authenticated');
            return;
        }

        if (!addUserForm.user_id) {
            if (showToast) showToast('error', 'Please select a user');
            return;
        }

        const groupId = addUserForm.group_id;

        setSubmitting(true);
        setApiError(null);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/centralization.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'add_user_to_group',
                    group_id: groupId,
                    user_id: addUserForm.user_id,
                    assigned_by: user.id,
                    access_level: addUserForm.access_level
                })
            });
            const data = await response.json();
            if (data.success) {
                if (showToast) showToast('success', 'User added to group');
                setShowAddUserModal(false);
                setAddUserForm({ group_id: '', user_id: '', access_level: 'user' });
                await fetchGroups();
                await fetchGroupDetails(groupId);
                setExpandedGroups(prev => ({ ...prev, [groupId]: true }));
            } else {
                if (showToast) showToast('error', data.error || 'Failed to add user');
            }
        } catch (error) {
            if (showToast) showToast('error', 'Network error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateGroup = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setApiError(null);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/centralization.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'update_group',
                    group_id: editGroupForm.group_id,
                    group_name: editGroupForm.group_name,
                    description: editGroupForm.description,
                    status: editGroupForm.status
                })
            });
            const data = await response.json();
            if (data.success) {
                if (showToast) showToast('success', 'Group updated successfully');
                setShowEditModal(false);
                await fetchGroups();
                if (selectedGroup) {
                    await fetchGroupDetails(selectedGroup.id);
                }
            } else {
                if (showToast) showToast('error', data.error || 'Failed to update group');
            }
        } catch (error) {
            if (showToast) showToast('error', 'Network error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteGroup = async (groupId) => {
        if (!window.confirm('Are you sure you want to delete this centralization group? All shops will be unlinked.')) return;

        setApiError(null);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/centralization.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'delete_group',
                    group_id: groupId
                })
            });
            const data = await response.json();
            if (data.success) {
                if (showToast) showToast('success', 'Group deleted successfully');
                setSelectedGroup(null);
                setGroupDetails(null);
                await fetchGroups();
            } else {
                if (showToast) showToast('error', data.error || 'Failed to delete group');
            }
        } catch (error) {
            if (showToast) showToast('error', 'Network error');
        }
    };

    // ==============================================
    // HANDLERS - Quick & Bulk Assignment
    // ==============================================

    const openQuickAssign = async (type, groupId) => {
        setQuickAssignForm({ type, group_id: groupId, items: [], search: '', selectAll: false });
        setSelectedItems([]);
        
        if (type === 'shop') {
            await fetchAvailableShops(groupId);
        } else if (type === 'user') {
            await fetchUsersWithRoles();
            // Filter users not already in group
            if (groupId && selectedGroup) {
                const existingUserIds = (selectedGroup.users || []).map(u => u.user_id || u.id);
                setAvailableUsers(prev => prev.filter(u => !existingUserIds.includes(u.id)));
            }
        } else if (type === 'product') {
            await fetchAvailableProducts(groupId);
        }
        setShowQuickAssignModal(true);
        setTimeout(() => {
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }, 100);
    };

    const handleQuickAssignSearch = (e) => {
        const value = e.target.value;
        setQuickAssignForm(prev => ({ ...prev, search: value }));
        
        // Filter available items based on search
        if (quickAssignForm.type === 'shop') {
            fetchAvailableShops(quickAssignForm.group_id, value);
        } else if (quickAssignForm.type === 'product') {
            fetchAvailableProducts(quickAssignForm.group_id, assignProductForm.product_table || 'mechanics_products', value);
        }
    };

    const toggleItemSelection = (itemId) => {
        setQuickAssignForm(prev => {
            const items = prev.items.includes(itemId)
                ? prev.items.filter(id => id !== itemId)
                : [...prev.items, itemId];
            return { ...prev, items };
        });
    };

    const toggleSelectAll = () => {
        const currentItems = getCurrentAvailableItems();
        const allIds = currentItems.map(item => item.id || item.shop_id || item.user_id);
        
        setQuickAssignForm(prev => {
            const isAllSelected = allIds.every(id => prev.items.includes(id));
            return {
                ...prev,
                items: isAllSelected ? [] : allIds,
                selectAll: !isAllSelected
            };
        });
    };

    const getCurrentAvailableItems = () => {
        if (quickAssignForm.type === 'shop') {
            return availableShops;
        } else if (quickAssignForm.type === 'user') {
            return availableUsers;
        } else if (quickAssignForm.type === 'product') {
            return availableProducts;
        }
        return [];
    };

    const getItemDisplayName = (item, type) => {
        if (type === 'shop') {
            return item.shop_name || 'Unknown Shop';
        } else if (type === 'user') {
            return item.fullname || item.username || 'Unknown User';
        } else if (type === 'product') {
            return item.product_name || `Product #${item.id}`;
        }
        return 'Unknown';
    };

    const getItemSubInfo = (item, type) => {
        if (type === 'shop') {
            return item.shop_code || '';
        } else if (type === 'user') {
            return item.role || '';
        } else if (type === 'product') {
            return `${item.product_code || ''} (Qty: ${item.quantity || 0})`;
        }
        return '';
    };

    const getItemIcon = (type) => {
        if (type === 'shop') return faStore;
        if (type === 'user') return faUserCircle;
        return faBox;
    };

    const handleQuickAssignSubmit = async (e) => {
        e.preventDefault();
        if (!isUserAvailable) {
            if (showToast) showToast('error', 'User not authenticated');
            return;
        }

        if (!quickAssignForm.group_id || !quickAssignForm.items || quickAssignForm.items.length === 0) {
            if (showToast) showToast('error', 'Please select items to assign');
            return;
        }

        setSubmitting(true);
        setApiError(null);

        try {
            const { type, group_id, items } = quickAssignForm;
            let successCount = 0;
            let errorCount = 0;

            if (type === 'shop') {
                const response = await fetch('https://ourzestapp.online/oz_backend/centralization.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'add_shops_to_group',
                        group_id: group_id,
                        shop_ids: JSON.stringify(items),
                        added_by: user.id
                    })
                });
                const data = await response.json();
                if (data.success) {
                    successCount = items.length;
                    if (showToast) showToast('success', `${successCount} shops added successfully`);
                } else {
                    if (showToast) showToast('error', data.error || 'Failed to add shops');
                }
            } else if (type === 'user') {
                for (const userId of items) {
                    const response = await fetch('https://ourzestapp.online/oz_backend/centralization.php', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            action: 'add_user_to_group',
                            group_id: group_id,
                            user_id: userId,
                            assigned_by: user.id,
                            access_level: 'user'
                        })
                    });
                    const data = await response.json();
                    if (data.success) {
                        successCount++;
                    } else {
                        errorCount++;
                    }
                }
                if (successCount > 0) {
                    if (showToast) showToast('success', `${successCount} users added${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
                } else {
                    if (showToast) showToast('error', 'Failed to add users');
                }
            } else if (type === 'product') {
                for (const productId of items) {
                    const response = await fetch('https://ourzestapp.online/oz_backend/centralization.php', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            action: 'assign_product',
                            product_id: productId,
                            product_table: assignProductForm.product_table || 'mechanics_products',
                            group_id: group_id,
                            assigned_by: user.id,
                            status: 'active'
                        })
                    });
                    const data = await response.json();
                    if (data.success) {
                        successCount++;
                    } else {
                        errorCount++;
                    }
                }
                if (successCount > 0) {
                    if (showToast) showToast('success', `${successCount} products assigned${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
                } else {
                    if (showToast) showToast('error', 'Failed to assign products');
                }
            }

            if (successCount > 0) {
                setShowQuickAssignModal(false);
                setQuickAssignForm({ type: 'shop', group_id: '', items: [], search: '', selectAll: false });
                await fetchGroups();
                await fetchGroupDetails(group_id);
                setExpandedGroups(prev => ({ ...prev, [group_id]: true }));
            }
        } catch (error) {
            console.error('Quick assign error:', error);
            if (showToast) showToast('error', 'Network error');
        } finally {
            setSubmitting(false);
        }
    };

    const openRemoveConfirm = (type, id, groupId, name) => {
        setRemoveItem({ type, id, group_id: groupId, name });
        setShowRemoveConfirmModal(true);
    };

    const openGroupDetails = async (groupId) => {
        const details = await fetchGroupDetails(groupId);
        if (details) {
            setShowGroupDetailsModal(true);
            await fetchStockMovements(groupId);
        }
    };

    const toggleGroupExpand = async (groupId) => {
        const willExpand = !expandedGroups[groupId];
        setExpandedGroups(prev => ({
            ...prev,
            [groupId]: willExpand
        }));
        if (willExpand) {
            await fetchGroupDetails(groupId);
        }
    };

    // ==============================================
    // EFFECTS
    // ==============================================
    
    useEffect(() => {
        if (isUserAvailable) {
            fetchGroups();
            fetchUsersWithRoles();
        } else {
            setLoading(false);
        }
    }, [isUserAvailable]);

    useEffect(() => {
        if (showCreateModal && isUserAvailable) {
            fetchAvailableShops();
        }
    }, [showCreateModal, fetchAvailableShops, isUserAvailable]);

    useEffect(() => {
        if (showAddShopModal && addShopForm.group_id && isUserAvailable) {
            fetchAvailableShops(addShopForm.group_id);
        }
    }, [showAddShopModal, addShopForm.group_id, fetchAvailableShops, isUserAvailable]);

    useEffect(() => {
        if (showAssignProductModal && assignProductForm.group_id && isUserAvailable) {
            fetchAvailableProducts(assignProductForm.group_id, assignProductForm.product_table);
        }
    }, [showAssignProductModal, assignProductForm.group_id, assignProductForm.product_table, fetchAvailableProducts, isUserAvailable]);

    // ==============================================
    // RENDER HELPERS
    // ==============================================
    
    const isRTL = document.documentElement.dir === 'rtl';
    
    const getStatusBadge = (status) => {
        const classes = {
            active: 'centralization-status-badge active',
            inactive: 'centralization-status-badge inactive',
            pending: 'centralization-status-badge pending'
        };
        return <span className={classes[status] || classes.pending}>{status || 'pending'}</span>;
    };

    const getAccessLevelBadge = (level) => {
        const colors = {
            admin: 'badge-danger',
            manager: 'badge-warning',
            user: 'badge-info',
            viewer: 'badge-secondary'
        };
        return <span className={`badge ${colors[level] || 'badge-secondary'}`}>{level || 'user'}</span>;
    };

    // ==============================================
    // MAIN RENDER
    // ==============================================
    
    if (!isUserAvailable) {
        return (
            <div className="centralization-container">
                <div className="centralization-empty">
                    <FontAwesomeIcon icon={faLayerGroup} />
                    <p>Please login to access centralization management</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`centralization-container ${darkMode ? 'centralization-dark' : ''} ${isRTL ? 'centralization-rtl' : ''}`}>
            {/* Error Banner */}
            {apiError && (
                <div className="centralization-error-banner">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <span>{apiError}</span>
                    <button onClick={() => setApiError(null)}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
            )}

            {/* Header */}
            <div className="centralization-header">
                <div className="centralization-header-content">
                    <div className="centralization-header-left">
                        <div className="centralization-header-icon">
                            <FontAwesomeIcon icon={faLayerGroup} />
                        </div>
                        <div className="centralization-header-text">
                            <h1>Centralization Manager</h1>
                            <p>Manage centralized shops, users, and products across multiple locations</p>
                        </div>
                    </div>
                    <div className="centralization-header-right">
                        <button
                            className="centralization-btn centralization-btn-primary"
                            onClick={() => setShowCreateModal(true)}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            <span>New Group</span>
                        </button>
                        <button
                            className="centralization-btn centralization-btn-secondary"
                            onClick={() => { fetchGroups(); fetchUsersWithRoles(); }}
                            disabled={loading || loadingUsers}
                        >
                            <FontAwesomeIcon icon={faSync} spin={loading || loadingUsers} />
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats Summary */}
            <div className="centralization-stats">
                <div className="centralization-stat-card">
                    <div className="centralization-stat-icon primary">
                        <FontAwesomeIcon icon={faLayerGroup} />
                    </div>
                    <div className="centralization-stat-info">
                        <h3>{groups.length}</h3>
                        <p>Total Groups</p>
                    </div>
                </div>
                <div className="centralization-stat-card">
                    <div className="centralization-stat-icon success">
                        <FontAwesomeIcon icon={faStore} />
                    </div>
                    <div className="centralization-stat-info">
                        <h3>{groups.reduce((sum, g) => sum + (g.shop_count || 0), 0)}</h3>
                        <p>Centralized Shops</p>
                    </div>
                </div>
                <div className="centralization-stat-card">
                    <div className="centralization-stat-icon info">
                        <FontAwesomeIcon icon={faUsers} />
                    </div>
                    <div className="centralization-stat-info">
                        <h3>{groups.reduce((sum, g) => sum + (g.user_count || 0), 0)}</h3>
                        <p>Users in Groups</p>
                    </div>
                </div>
                <div className="centralization-stat-card">
                    <div className="centralization-stat-icon warning">
                        <FontAwesomeIcon icon={faUserTag} />
                    </div>
                    <div className="centralization-stat-info">
                        <h3>{allUsersWithRoles.length}</h3>
                        <p>Total Users</p>
                    </div>
                </div>
            </div>

            {/* Groups List */}
            <div className="centralization-groups-list">
                {loading ? (
                    <div className="centralization-loading">
                        <div className="centralization-loading-spinner"></div>
                        <p>Loading groups...</p>
                    </div>
                ) : groups.length === 0 ? (
                    <div className="centralization-empty">
                        <FontAwesomeIcon icon={faLayerGroup} />
                        <p>No centralization groups created yet</p>
                        <button
                            className="centralization-btn centralization-btn-primary"
                            onClick={() => setShowCreateModal(true)}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            Create Your First Group
                        </button>
                    </div>
                ) : (
                    groups.map(group => (
                        <div key={group.id} className="centralization-group-card">
                            <div
                                className="centralization-group-header"
                                onClick={() => toggleGroupExpand(group.id)}
                            >
                                <div className="centralization-group-header-left">
                                    <div className="centralization-group-icon">
                                        <FontAwesomeIcon icon={faLayerGroup} />
                                    </div>
                                    <div className="centralization-group-info">
                                        <h3>{group.group_name}</h3>
                                        <div className="centralization-group-meta">
                                            <span className="centralization-group-code">
                                                <FontAwesomeIcon icon={faTags} />
                                                {group.group_code}
                                            </span>
                                            <span className="centralization-group-shops">
                                                <FontAwesomeIcon icon={faStore} />
                                                {group.shop_count || 0} shops
                                            </span>
                                            <span className="centralization-group-users">
                                                <FontAwesomeIcon icon={faUsers} />
                                                {group.user_count || 0} users
                                            </span>
                                            {getStatusBadge(group.status)}
                                        </div>
                                    </div>
                                </div>
                                <div className="centralization-group-header-right">
                                    <button
                                        className="centralization-action-btn"
                                        onClick={(e) => { 
                                            e.stopPropagation(); 
                                            setEditGroupForm({ 
                                                group_id: group.id, 
                                                group_name: group.group_name, 
                                                description: group.description || '', 
                                                status: group.status || 'active' 
                                            });
                                            setShowEditModal(true);
                                        }}
                                        title="Edit Group"
                                    >
                                        <FontAwesomeIcon icon={faEdit} />
                                    </button>
                                    <button
                                        className="centralization-action-btn centralization-action-info"
                                        onClick={(e) => { e.stopPropagation(); openGroupDetails(group.id); }}
                                        title="View Details"
                                    >
                                        <FontAwesomeIcon icon={faEye} />
                                    </button>
                                    <button
                                        className="centralization-action-btn centralization-action-danger"
                                        onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group.id); }}
                                        title="Delete Group"
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                    <button className="centralization-expand-btn">
                                        <FontAwesomeIcon icon={expandedGroups[group.id] ? faChevronUp : faChevronDown} />
                                    </button>
                                </div>
                            </div>

                            {expandedGroups[group.id] && selectedGroup && selectedGroup.id === group.id && (
                                <div className="centralization-group-details">
                                    {/* Quick Actions - Easy Assignment Buttons */}
                                    <div className="centralization-quick-actions">
                                        <button
                                            className="centralization-btn centralization-btn-primary centralization-btn-with-icon"
                                            onClick={() => openQuickAssign('shop', group.id)}
                                        >
                                            <FontAwesomeIcon icon={faStore} />
                                            <span>Quick Add Shops</span>
                                            <small className="btn-hint">Batch add multiple shops</small>
                                        </button>
                                        <button
                                            className="centralization-btn centralization-btn-primary centralization-btn-with-icon"
                                            onClick={() => openQuickAssign('user', group.id)}
                                        >
                                            <FontAwesomeIcon icon={faUserPlus} />
                                            <span>Quick Add Users</span>
                                            <small className="btn-hint">Batch add multiple users</small>
                                        </button>
                                        <button
                                            className="centralization-btn centralization-btn-primary centralization-btn-with-icon"
                                            onClick={() => openQuickAssign('product', group.id)}
                                        >
                                            <FontAwesomeIcon icon={faBox} />
                                            <span>Quick Assign Products</span>
                                            <small className="btn-hint">Batch assign products</small>
                                        </button>
                                    </div>

                                    {/* Shops Section */}
                                    <div className="centralization-section">
                                        <div className="centralization-section-header">
                                            <h4>
                                                <FontAwesomeIcon icon={faStore} />
                                                Shops in Group
                                                <span className="centralization-count-badge">
                                                    {(selectedGroup.shops || []).length}
                                                </span>
                                            </h4>
                                            <button
                                                className="centralization-btn centralization-btn-small centralization-btn-secondary"
                                                onClick={() => openQuickAssign('shop', group.id)}
                                            >
                                                <FontAwesomeIcon icon={faPlus} /> Add Shops
                                            </button>
                                        </div>
                                        <div className="centralization-items-grid">
                                            {(selectedGroup.shops || []).length === 0 ? (
                                                <div className="centralization-empty-small">
                                                    <FontAwesomeIcon icon={faStore} />
                                                    <p>No shops in this group yet</p>
                                                    <button
                                                        className="centralization-btn centralization-btn-small centralization-btn-primary"
                                                        onClick={() => openQuickAssign('shop', group.id)}
                                                    >
                                                        <FontAwesomeIcon icon={faPlus} /> Add Shops
                                                    </button>
                                                </div>
                                            ) : (
                                                selectedGroup.shops.map(shop => (
                                                    <div key={shop.shop_id} className="centralization-item-card">
                                                        <div className="centralization-item-info">
                                                            <div className="centralization-item-icon-wrapper">
                                                                <FontAwesomeIcon icon={faStore} className="centralization-item-icon" />
                                                            </div>
                                                            <div className="centralization-item-details">
                                                                <strong className="centralization-item-name">{shop.shop_name}</strong>
                                                                <span className="centralization-item-code">{shop.shop_code}</span>
                                                                {shop.is_centralized && (
                                                                    <span className="centralization-item-badge">Centralized</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="centralization-item-actions">
                                                            <button
                                                                className="centralization-action-btn centralization-action-danger"
                                                                onClick={() => openRemoveConfirm('shop', shop.shop_id, group.id, shop.shop_name)}
                                                                title="Remove from group"
                                                            >
                                                                <FontAwesomeIcon icon={faUnlink} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>

                                    {/* Users Section */}
                                    <div className="centralization-section">
                                        <div className="centralization-section-header">
                                            <h4>
                                                <FontAwesomeIcon icon={faUsers} />
                                                Users in Group
                                                <span className="centralization-count-badge">
                                                    {(selectedGroup.users || []).length}
                                                </span>
                                            </h4>
                                            <button
                                                className="centralization-btn centralization-btn-small centralization-btn-secondary"
                                                onClick={() => openQuickAssign('user', group.id)}
                                            >
                                                <FontAwesomeIcon icon={faPlus} /> Add Users
                                            </button>
                                        </div>
                                        <div className="centralization-items-grid">
                                            {(selectedGroup.users || []).length === 0 ? (
                                                <div className="centralization-empty-small">
                                                    <FontAwesomeIcon icon={faUsers} />
                                                    <p>No users in this group yet</p>
                                                    <button
                                                        className="centralization-btn centralization-btn-small centralization-btn-primary"
                                                        onClick={() => openQuickAssign('user', group.id)}
                                                    >
                                                        <FontAwesomeIcon icon={faPlus} /> Add Users
                                                    </button>
                                                </div>
                                            ) : (
                                                selectedGroup.users.map(userItem => (
                                                    <div key={userItem.id || userItem.user_id} className="centralization-item-card">
                                                        <div className="centralization-item-info">
                                                            <div className="centralization-user-avatar-wrapper">
                                                                <div className="centralization-user-avatar">
                                                                    {userItem.fullname ? userItem.fullname.charAt(0).toUpperCase() : 'U'}
                                                                </div>
                                                            </div>
                                                            <div className="centralization-item-details">
                                                                <strong className="centralization-item-name">{userItem.fullname || userItem.username}</strong>
                                                                <span className="centralization-item-role">{userItem.role || 'No role'}</span>
                                                                {userItem.shop_roles && userItem.shop_roles.length > 0 && (
                                                                    <span className="centralization-user-shop-roles">
                                                                        {userItem.shop_roles.slice(0, 2).map((sr, idx) => (
                                                                            <span key={idx} className="shop-role-tag">{sr.role}</span>
                                                                        ))}
                                                                        {userItem.shop_roles.length > 2 && (
                                                                            <span className="shop-role-tag">+{userItem.shop_roles.length - 2}</span>
                                                                        )}
                                                                    </span>
                                                                )}
                                                                {getAccessLevelBadge(userItem.access_level)}
                                                            </div>
                                                        </div>
                                                        <div className="centralization-item-actions">
                                                            <button
                                                                className="centralization-action-btn centralization-action-danger"
                                                                onClick={() => openRemoveConfirm('user', userItem.user_id || userItem.id, group.id, userItem.fullname || userItem.username)}
                                                                title="Remove from group"
                                                            >
                                                                <FontAwesomeIcon icon={faUserMinus} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>

                                    {/* Products Section */}
                                    <div className="centralization-section">
                                        <div className="centralization-section-header">
                                            <h4>
                                                <FontAwesomeIcon icon={faBox} />
                                                Centralized Products
                                                <span className="centralization-count-badge">
                                                    {(selectedGroup.centralized_products || []).length}
                                                </span>
                                            </h4>
                                            <button
                                                className="centralization-btn centralization-btn-small centralization-btn-secondary"
                                                onClick={() => openQuickAssign('product', group.id)}
                                            >
                                                <FontAwesomeIcon icon={faPlus} /> Assign Products
                                            </button>
                                        </div>
                                        <div className="centralization-items-grid">
                                            {(selectedGroup.centralized_products || []).length === 0 ? (
                                                <div className="centralization-empty-small">
                                                    <FontAwesomeIcon icon={faBox} />
                                                    <p>No products centralized yet</p>
                                                    <button
                                                        className="centralization-btn centralization-btn-small centralization-btn-primary"
                                                        onClick={() => openQuickAssign('product', group.id)}
                                                    >
                                                        <FontAwesomeIcon icon={faPlus} /> Assign Products
                                                    </button>
                                                </div>
                                            ) : (
                                                selectedGroup.centralized_products.map(product => (
                                                    <div key={product.id} className="centralization-item-card">
                                                        <div className="centralization-item-info">
                                                            <div className="centralization-item-icon-wrapper">
                                                                <FontAwesomeIcon icon={faBox} className="centralization-item-icon" />
                                                            </div>
                                                            <div className="centralization-item-details">
                                                                <strong className="centralization-item-name">Product #{product.product_id}</strong>
                                                                <span className="centralization-item-table">{product.product_table}</span>
                                                                {getStatusBadge(product.status)}
                                                            </div>
                                                        </div>
                                                        <div className="centralization-item-actions">
                                                            <button
                                                                className="centralization-action-btn centralization-action-danger"
                                                                onClick={() => openRemoveConfirm('product', product.id, group.id, `Product #${product.product_id}`)}
                                                                title="Remove from centralization"
                                                            >
                                                                <FontAwesomeIcon icon={faUnlink} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>

                                    {/* Description */}
                                    {selectedGroup.description && (
                                        <div className="centralization-section">
                                            <div className="centralization-section-header">
                                                <h4>
                                                    <FontAwesomeIcon icon={faEdit} />
                                                    Description
                                                </h4>
                                            </div>
                                            <p className="centralization-description">{selectedGroup.description}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            {/* ==============================================
                MODALS
                ============================================== */}
            
            {/* Create Group Modal */}
            {showCreateModal && (
                <div className="centralization-modal-overlay" onClick={() => !submitting && setShowCreateModal(false)}>
                    <div className="centralization-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="centralization-modal-header">
                            <h3>
                                <FontAwesomeIcon icon={faPlus} />
                                Create Centralization Group
                            </h3>
                            <button className="centralization-modal-close" onClick={() => setShowCreateModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={handleCreateGroup}>
                            <div className="centralization-form-group">
                                <label>Group Name <span className="centralization-required">*</span></label>
                                <input
                                    type="text"
                                    value={groupForm.group_name}
                                    onChange={(e) => setGroupForm({ ...groupForm, group_name: e.target.value })}
                                    placeholder="e.g., Main Branch Centralization"
                                    required
                                    disabled={submitting}
                                />
                            </div>
                            <div className="centralization-form-group">
                                <label>Group Code <span className="centralization-required">*</span></label>
                                <input
                                    type="text"
                                    value={groupForm.group_code}
                                    onChange={(e) => setGroupForm({ ...groupForm, group_code: e.target.value.toUpperCase() })}
                                    placeholder="e.g., MAIN_01"
                                    required
                                    disabled={submitting}
                                />
                                <small>Unique identifier for this group</small>
                            </div>
                            <div className="centralization-form-group">
                                <label>Description</label>
                                <textarea
                                    value={groupForm.description}
                                    onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                                    placeholder="Optional description"
                                    rows="3"
                                    disabled={submitting}
                                />
                            </div>
                            <div className="centralization-form-group">
                                <label>Select Shops to Centralize</label>
                                <div className="centralization-shop-select-grid">
                                    {availableShops.length === 0 ? (
                                        <p className="centralization-text-muted">No shops available to centralize</p>
                                    ) : (
                                        availableShops.map(shop => (
                                            <label key={shop.shop_id} className="centralization-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={groupForm.shop_ids.includes(shop.shop_id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setGroupForm({
                                                                ...groupForm,
                                                                shop_ids: [...groupForm.shop_ids, shop.shop_id]
                                                            });
                                                        } else {
                                                            setGroupForm({
                                                                ...groupForm,
                                                                shop_ids: groupForm.shop_ids.filter(id => id !== shop.shop_id)
                                                            });
                                                        }
                                                    }}
                                                    disabled={submitting}
                                                />
                                                <span className="centralization-checkbox-text">
                                                    <FontAwesomeIcon icon={faStore} />
                                                    {shop.shop_name}
                                                    <small>{shop.shop_code}</small>
                                                </span>
                                            </label>
                                        ))
                                    )}
                                </div>
                            </div>
                            <div className="centralization-form-actions">
                                <button
                                    type="button"
                                    className="centralization-btn centralization-btn-secondary"
                                    onClick={() => setShowCreateModal(false)}
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="centralization-btn centralization-btn-primary"
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <div className="centralization-btn-spinner"></div>
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSave} />
                                            Create Group
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Group Modal */}
            {showEditModal && (
                <div className="centralization-modal-overlay" onClick={() => !submitting && setShowEditModal(false)}>
                    <div className="centralization-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="centralization-modal-header">
                            <h3>
                                <FontAwesomeIcon icon={faEdit} />
                                Edit Group
                            </h3>
                            <button className="centralization-modal-close" onClick={() => setShowEditModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={handleUpdateGroup}>
                            <div className="centralization-form-group">
                                <label>Group Name <span className="centralization-required">*</span></label>
                                <input
                                    type="text"
                                    value={editGroupForm.group_name}
                                    onChange={(e) => setEditGroupForm({ ...editGroupForm, group_name: e.target.value })}
                                    required
                                    disabled={submitting}
                                />
                            </div>
                            <div className="centralization-form-group">
                                <label>Description</label>
                                <textarea
                                    value={editGroupForm.description}
                                    onChange={(e) => setEditGroupForm({ ...editGroupForm, description: e.target.value })}
                                    rows="3"
                                    disabled={submitting}
                                />
                            </div>
                            <div className="centralization-form-group">
                                <label>Status</label>
                                <select
                                    value={editGroupForm.status}
                                    onChange={(e) => setEditGroupForm({ ...editGroupForm, status: e.target.value })}
                                    disabled={submitting}
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                            <div className="centralization-form-actions">
                                <button
                                    type="button"
                                    className="centralization-btn centralization-btn-secondary"
                                    onClick={() => setShowEditModal(false)}
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="centralization-btn centralization-btn-primary"
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <div className="centralization-btn-spinner"></div>
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSave} />
                                            Update Group
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Quick Assign Modal - Enhanced */}
            {showQuickAssignModal && (
                <div className="centralization-modal-overlay" onClick={() => !submitting && setShowQuickAssignModal(false)}>
                    <div className="centralization-modal centralization-modal-large" onClick={(e) => e.stopPropagation()} ref={modalRef}>
                        <div className="centralization-modal-header">
                            <h3>
                                <FontAwesomeIcon icon={faRocket} />
                                Quick Assign {quickAssignForm.type === 'shop' ? 'Shops' : quickAssignForm.type === 'user' ? 'Users' : 'Products'}
                            </h3>
                            <button className="centralization-modal-close" onClick={() => setShowQuickAssignModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="centralization-modal-body">
                            <p className="centralization-form-hint">
                                <FontAwesomeIcon icon={faInfoCircle} />
                                Select multiple {quickAssignForm.type === 'shop' ? 'shops' : quickAssignForm.type === 'user' ? 'users' : 'products'} to assign to this group.
                                {quickAssignForm.type === 'shop' && ' All selected shops will be centralized.'}
                                {quickAssignForm.type === 'user' && ' Selected users will gain access to all shops in the group.'}
                                {quickAssignForm.type === 'product' && ' Selected products will be synchronized across all shops in the group.'}
                            </p>
                            
                            {/* Search and Select All */}
                            <div className="centralization-quick-assign-controls">
                                <div className="centralization-search-wrapper">
                                    <FontAwesomeIcon icon={faSearch} className="centralization-search-icon" />
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        placeholder={`Search ${quickAssignForm.type === 'shop' ? 'shops' : quickAssignForm.type === 'user' ? 'users' : 'products'}...`}
                                        value={quickAssignForm.search}
                                        onChange={handleQuickAssignSearch}
                                        className="centralization-search-input"
                                        disabled={submitting}
                                    />
                                    {quickAssignForm.search && (
                                        <button 
                                            className="centralization-search-clear"
                                            onClick={() => {
                                                setQuickAssignForm(prev => ({ ...prev, search: '' }));
                                                if (quickAssignForm.type === 'shop') fetchAvailableShops(quickAssignForm.group_id);
                                                else if (quickAssignForm.type === 'product') fetchAvailableProducts(quickAssignForm.group_id);
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    )}
                                </div>
                                <div className="centralization-select-all-wrapper">
                                    <label className="centralization-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={quickAssignForm.selectAll}
                                            onChange={toggleSelectAll}
                                            disabled={submitting || getCurrentAvailableItems().length === 0}
                                        />
                                        <span>Select All</span>
                                        <span className="centralization-select-all-count">
                                            ({getCurrentAvailableItems().length})
                                        </span>
                                    </label>
                                </div>
                            </div>

                            {/* Items Grid */}
                            <div className="centralization-quick-assign-grid">
                                {(() => {
                                    const items = getCurrentAvailableItems();
                                    if (items.length === 0) {
                                        return (
                                            <div className="centralization-empty-small">
                                                <FontAwesomeIcon icon={faSearch} />
                                                <p>No {quickAssignForm.type === 'shop' ? 'shops' : quickAssignForm.type === 'user' ? 'users' : 'products'} available</p>
                                                {quickAssignForm.search && (
                                                    <p className="centralization-text-muted">Try adjusting your search</p>
                                                )}
                                            </div>
                                        );
                                    }
                                    return items.map(item => {
                                        const itemId = item.id || item.shop_id || item.user_id;
                                        const isSelected = quickAssignForm.items.includes(itemId);
                                        const displayName = getItemDisplayName(item, quickAssignForm.type);
                                        const subInfo = getItemSubInfo(item, quickAssignForm.type);
                                        const icon = getItemIcon(quickAssignForm.type);
                                        
                                        return (
                                            <label key={itemId} className={`centralization-quick-item ${isSelected ? 'selected' : ''}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleItemSelection(itemId)}
                                                    disabled={submitting}
                                                />
                                                <div className="centralization-quick-item-content">
                                                    <div className="centralization-quick-item-icon">
                                                        <FontAwesomeIcon icon={icon} />
                                                    </div>
                                                    <div className="centralization-quick-item-info">
                                                        <span className="centralization-quick-item-name">{displayName}</span>
                                                        {subInfo && (
                                                            <span className="centralization-quick-item-sub">{subInfo}</span>
                                                        )}
                                                    </div>
                                                    {isSelected && (
                                                        <div className="centralization-quick-item-checked">
                                                            <FontAwesomeIcon icon={faCheck} />
                                                        </div>
                                                    )}
                                                </div>
                                            </label>
                                        );
                                    });
                                })()}
                            </div>

                            {/* Selected Count */}
                            <div className="centralization-quick-assign-footer">
                                <div className="centralization-selected-count">
                                    <FontAwesomeIcon icon={faCheck} />
                                    <span>{quickAssignForm.items.length} items selected</span>
                                </div>
                                <div className="centralization-form-actions">
                                    <button
                                        type="button"
                                        className="centralization-btn centralization-btn-secondary"
                                        onClick={() => setShowQuickAssignModal(false)}
                                        disabled={submitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="centralization-btn centralization-btn-primary"
                                        onClick={handleQuickAssignSubmit}
                                        disabled={submitting || quickAssignForm.items.length === 0}
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="centralization-btn-spinner"></div>
                                                Assigning...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faCheck} />
                                                Assign Selected ({quickAssignForm.items.length})
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove Confirm Modal */}
            {showRemoveConfirmModal && (
                <div className="centralization-modal-overlay" onClick={() => !submitting && setShowRemoveConfirmModal(false)}>
                    <div className="centralization-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="centralization-modal-header centralization-modal-header-danger">
                            <h3>
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                Confirm Removal
                            </h3>
                            <button className="centralization-modal-close" onClick={() => setShowRemoveConfirmModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="centralization-modal-body">
                            <p>Are you sure you want to remove <strong>{removeItem.name}</strong> from this group?</p>
                            <p className="centralization-text-muted">This action can be undone by adding it back later.</p>
                        </div>
                        <div className="centralization-form-actions">
                            <button
                                type="button"
                                className="centralization-btn centralization-btn-secondary"
                                onClick={() => setShowRemoveConfirmModal(false)}
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="centralization-btn centralization-btn-danger"
                                onClick={handleRemoveFromGroup}
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <div className="centralization-btn-spinner"></div>
                                        Removing...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faTrash} />
                                        Remove
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Group Details Modal */}
            {showGroupDetailsModal && groupDetails && (
                <div className="centralization-modal-overlay" onClick={() => setShowGroupDetailsModal(false)}>
                    <div className="centralization-modal centralization-modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="centralization-modal-header">
                            <h3>
                                <FontAwesomeIcon icon={faLayerGroup} />
                                {groupDetails.group_name}
                                <span className="centralization-modal-subtitle">{groupDetails.group_code}</span>
                            </h3>
                            <button className="centralization-modal-close" onClick={() => setShowGroupDetailsModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="centralization-modal-body">
                            {/* Overview */}
                            <div className="centralization-details-overview">
                                <div className="detail-item">
                                    <span className="detail-label">Status</span>
                                    {getStatusBadge(groupDetails.status)}
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Shops</span>
                                    <span className="detail-value">{(groupDetails.shops || []).length}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Users</span>
                                    <span className="detail-value">{(groupDetails.users || []).length}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Products</span>
                                    <span className="detail-value">{(groupDetails.centralized_products || []).length}</span>
                                </div>
                            </div>

                            {groupDetails.description && (
                                <div className="centralization-details-description">
                                    <h4>Description</h4>
                                    <p>{groupDetails.description}</p>
                                </div>
                            )}

                            {/* Shops List */}
                            <div className="centralization-details-section">
                                <h4><FontAwesomeIcon icon={faStore} /> Shops in Group</h4>
                                <div className="centralization-details-grid">
                                    {(groupDetails.shops || []).map(shop => (
                                        <div key={shop.shop_id} className="detail-card">
                                            <strong>{shop.shop_name}</strong>
                                            <span>{shop.shop_code}</span>
                                        </div>
                                    ))}
                                    {(groupDetails.shops || []).length === 0 && (
                                        <p className="centralization-text-muted">No shops in this group</p>
                                    )}
                                </div>
                            </div>

                            {/* Users List */}
                            <div className="centralization-details-section">
                                <h4><FontAwesomeIcon icon={faUsers} /> Users in Group</h4>
                                <div className="centralization-details-grid">
                                    {(groupDetails.users || []).map(userItem => (
                                        <div key={userItem.id} className="detail-card">
                                            <strong>{userItem.fullname || userItem.username}</strong>
                                            <span>{userItem.role}</span>
                                            {getAccessLevelBadge(userItem.access_level)}
                                        </div>
                                    ))}
                                    {(groupDetails.users || []).length === 0 && (
                                        <p className="centralization-text-muted">No users in this group</p>
                                    )}
                                </div>
                            </div>

                            {/* Products List */}
                            <div className="centralization-details-section">
                                <h4><FontAwesomeIcon icon={faBox} /> Centralized Products</h4>
                                <div className="centralization-details-grid">
                                    {(groupDetails.centralized_products || []).map(product => (
                                        <div key={product.id} className="detail-card">
                                            <strong>Product #{product.product_id}</strong>
                                            <span>{product.product_table}</span>
                                            {getStatusBadge(product.status)}
                                        </div>
                                    ))}
                                    {(groupDetails.centralized_products || []).length === 0 && (
                                        <p className="centralization-text-muted">No products in this group</p>
                                    )}
                                </div>
                            </div>

                            {/* Stock Movements */}
                            {stockMovements.length > 0 && (
                                <div className="centralization-details-section">
                                    <h4><FontAwesomeIcon icon={faHistory} /> Recent Stock Movements</h4>
                                    <div className="centralization-movements-list">
                                        {stockMovements.slice(0, 10).map(movement => (
                                            <div key={movement.id} className="movement-item">
                                                <span className={`movement-type ${movement.movement_type}`}>
                                                    {movement.movement_type}
                                                </span>
                                                <span className="movement-quantity">
                                                    {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                                                </span>
                                                <span className="movement-shop">{movement.source_shop_name}</span>
                                                {movement.target_shop_name && (
                                                    <span className="movement-target">→ {movement.target_shop_name}</span>
                                                )}
                                                <span className="movement-date">
                                                    {new Date(movement.movement_date).toLocaleDateString()}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="centralization-form-actions">
                            <button
                                type="button"
                                className="centralization-btn centralization-btn-primary"
                                onClick={() => setShowGroupDetailsModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CentralizationManager;